import { supabase } from '@/integrations/supabase/client';

/**
 * Checks whether a user has active specialty access for a program.
 * Access is active when an access grant exists and is not expired.
 */
export async function hasActiveSpecialtyProgramAccess(
  userId: string,
  programId: string
): Promise<boolean> {
  const { data, error } = await supabase
    .from('specialty_program_access')
    .select('id, expires_at')
    .eq('user_id', userId)
    .eq('program_id', programId)
    .order('granted_at', { ascending: false })
    .limit(5);

  if (error) {
    console.warn('Failed to check specialty program access:', error);
    return false;
  }

  if (!data || data.length === 0) return false;

  const now = Date.now();
  return data.some((record) => {
    if (!record.expires_at) return true;
    const expiresAt = new Date(record.expires_at).getTime();
    return Number.isFinite(expiresAt) && expiresAt > now;
  });
}

/**
 * Checks the canonical database access rule for a program.
 * This includes free/Premium/specialty access plus journey-scoped PowerPrompts
 * unlocked by a purchased journey.
 */
export async function hasActiveProgramAccess(
  userId: string,
  programId: string
): Promise<boolean> {
  const { data, error } = await supabase.rpc('user_has_program_access', {
    p_user_id: userId,
    p_program_id: programId,
  });

  if (error) {
    console.warn('Failed to check program access:', error);
    return false;
  }

  return Boolean(data);
}

/**
 * The same server rule as hasActiveProgramAccess, but a failed call throws
 * instead of reading as "no access", so a caller can offer a retry rather
 * than the paywall.
 */
export async function fetchProgramAccessAnswer(userId: string, programId: string): Promise<boolean> {
  const { data, error } = await supabase.rpc('user_has_program_access', {
    p_user_id: userId,
    p_program_id: programId,
  });
  if (error) throw error;
  return Boolean(data);
}

/**
 * Checks whether a user has a completed content purchase for a program
 * (a la carte purchase or admin-granted access via content_purchases).
 */
export async function hasActiveContentPurchase(
  userId: string,
  programId: string,
  contentType: 'course' | 'challenge'
): Promise<boolean> {
  const { data, error } = await supabase
    .from('content_purchases')
    .select('id')
    .eq('user_id', userId)
    .eq('content_type', contentType)
    .eq('content_id', programId)
    .eq('status', 'completed')
    .maybeSingle();

  if (error) {
    console.warn('Failed to check content purchase access:', error);
    return false;
  }

  return Boolean(data?.id);
}
